import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { DialogModule } from 'primeng/dialog';

export type ModalType = 'comment' | 'approve' | 'reject' | 'upload' | 'custom' | 'confirm';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TextareaModule,
    DialogModule
  ],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss'
})
export class ModalComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() modalType: ModalType = 'custom';
  @Input() title: string = '';
  @Input() message: string = ''; // For confirmation dialogs
  @Input() confirmButtonText: string = 'Confirm';
  @Input() cancelButtonText: string = 'Cancel';
  @Input() confirmButtonDisabled: boolean = false; // For disabling confirm button
  /** Primary action label for comment / approve / reject forms (default Submit). */
  @Input() submitButtonLabel: string = 'Submit';
  @Input() width: string = '500px';
  @Input() dismissableMask: boolean = true;
  @Input() closable: boolean = true;
  /**
   * If true, `submit` is emitted but the dialog is not closed (parent closes after async work).
   */
  @Input() keepOpenOnSubmit: boolean = false;
  /**
   * Disables the primary action and shows loading (e.g. long-running Sign and Submit).
   */
  @Input() submitInProgress: boolean = false;
  /** Prefill comment (e.g. DocuSign same-tab return with stored approval text). */
  @Input() commentInitialValue: string = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<void>();
  @Output() submit = new EventEmitter<any>();
  @Output() confirm = new EventEmitter<void>(); // For confirmation dialogs

  // Comment form data
  commentText: string = '';

  // Upload form data
  uploadedFiles: File[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['commentInitialValue']) {
      this.commentText = this.commentInitialValue ?? '';
    }
    if (changes['visible']?.currentValue && this.visible) {
      this.commentText = this.commentInitialValue ?? '';
    }
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.close.emit();
    this.resetForm();
  }

  onSubmit(): void {
    if (this.modalType === 'confirm') {
      this.confirm.emit();
      this.onClose();
      return;
    }

    const data = this.modalType === 'comment' || this.modalType === 'approve' || this.modalType === 'reject'
      ? { comment: this.commentText }
      : { files: this.uploadedFiles };

    this.submit.emit(data);
    if (!this.keepOpenOnSubmit) {
      this.onClose();
    }
  }

  onFileSelect(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.uploadedFiles = Array.from(target.files);
    }
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
  }

  private resetForm(): void {
    this.commentText = '';
    this.uploadedFiles = [];
  }

  onHide(): void {
    this.onClose();
  }
}
